import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parse } from 'date-fns';
import { enGB, fr, de, tr, az, ar, ru, Locale } from 'date-fns/locale';

interface DateTimePickerProps {
    label: string;
    inputId: string;
    value: string;
    onChange: (value: string) => void;
    showTime?: boolean;
    showOnlyTime?: boolean;
    required?: boolean;
    errorMessage?: string;
    disabled?:boolean;
}

const localeMap: Record<string, Locale> = {
    en: enGB,
    fr: fr,
    de: de,
    tr: tr,
    az: az,
    ar: ar,
    ru: ru,
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
    label,
    inputId,
    value,
    onChange,
    showTime = false,
    showOnlyTime = false,
    required = false,
    errorMessage,
    disabled=false
}) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        value ? parse(value, showOnlyTime ? "HH:mm:ss" : "yyyy-MM-dd", new Date()) : null
    );
    const [isValid, setIsValid] = useState(true);
    const [locale, setLocale] = useState<Locale>(localeMap['tr']);

    useEffect(() => {
        const storedLang = localStorage.getItem('selectedLang') || 'tr';
        const langCode = storedLang.split('-')[0];
        setLocale(localeMap[langCode] || localeMap['tr']);
    }, []);

    const handleDateChange = (date: Date | null) => {
        if (date) {
            const formattedDate = format(
                date,
                showOnlyTime ? "HH:mm:ss" : showTime ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd"
            );
            setSelectedDate(date);
            onChange(formattedDate);
            setIsValid(true);
        } else {
            setSelectedDate(null);
            onChange('');
        }
    };

    const validate = () => {
        if (required && !selectedDate) {
            setIsValid(false);
            return false;
        }
        setIsValid(true);
        return true;
    };

    return (
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
            <label className="form-label" htmlFor={inputId}>
                {label}
            </label>
            <DatePicker
                id={inputId}
                selected={selectedDate}
                onChange={handleDateChange}
                showTimeSelect={showTime && !showOnlyTime}
                showTimeSelectOnly={showOnlyTime}
                timeFormat="HH:mm"
                timeIntervals={1}
                dateFormat={showOnlyTime ? "HH:mm" : showTime ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
                locale={locale}
                className={`form-control ${!isValid ? 'is-invalid' : ''}`}
                disabled={disabled}
            />
            {!isValid && errorMessage && (
                <div className="invalid-feedback">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default DateTimePicker;
